import React from "react";
import { type GetStaticProps, type NextPage } from "next";
import {
  type Banner,
  type Customer,
  type IntegrationSimple,
  type Page as SanityPage,
  getAnnouncementBanner,
  getPage,
  getSimpleIntegrations,
  getStackedLogos,
} from "lib/sanity";
import { AspectRatio, Flex, SimpleGrid } from "@chakra-ui/react";
import { getPageMetaImage } from "utils";
import { Page } from "components/page";
import { HalfHeroAnimation } from "components/half-hero-animation";
import { StackedLogos } from "components/logos";
import { Section } from "components/section";
import { ImageFeatureCard } from "components/cards";
import { QuoteCard, CenteredQuote } from "components/quotes";
import { ProductPair } from "components/product-pair";
import { DemoEmbedHalf } from "components/demo-embed";
import { ComposableCdpDiagram } from "components/composable-cdp-diagram";
import DestinationMarquee from "components/destination-marquee";
import { SectionHeading } from "components/section-heading";
import { SplitsAnimation } from "components/animations";
import {
  ActivationAnimation,
  HeroAnimation,
  InsightsAnimation,
  JourneysAnimation,
  TraitsAnimation,
} from "components/platform/customer-studio/animations";
import { AnimationVideo } from "ui/animation-video";
import { RadialGradient } from "ui/radial-gradient";
import { GradientCard } from "ui/cards/gradient-card";
import { SecurityFeatures } from "components/content";
import { integrationsCount } from "../../constants/integrations";
import feature1Image from "../../../public/assets/platform/customer-studio/feature-1.png";
import feature2Image from "../../../public/assets/platform/customer-studio/feature-2.png";
import feature3Image from "../../../public/assets/platform/customer-studio/feature-3.png";
import activateMobile from "../../../public/assets/platform/customer-studio/activate-mobile.png";
import journeysMobile from "../../../public/assets/platform/customer-studio/journeys-mobile.png";
import insightsMobile from "../../../public/assets/platform/customer-studio/insights-mobile.png";
import traitsMobile from "../../../public/assets/platform/customer-studio/traits-mobile.png";
import splitsMobile from "../../../public/assets/animations/splits-mobile.png";

const productPairGap = { base: 12, lg: 16, xl: 24 };

type PageProps = {
  page: SanityPage;
  announcementBanner?: Banner;
  destinations: IntegrationSimple[];
  customerRows: Customer[][];
};

const CustomerStudio: NextPage<Readonly<PageProps>> = ({ page, announcementBanner, destinations, customerRows }) => {
  return (
    <Page
      hasSmoothScroll
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
    >
      <HalfHeroAnimation
        hideSecondary
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.description}
        gap={{ base: 12, md: 16, lg: 7, xl: 16 }}
        animationElement={<HeroAnimation />}
        backgroundGradient={
          <>
            <RadialGradient
              variant="mint"
              bottom={{ base: "72%", lg: "88%" }}
              right={{ base: "70%", lg: "40%" }}
              boxSize={{ base: "70%", lg: "80%" }}
              opacity={{ base: 0.2, xl: 0.6 }}
            />
            <RadialGradient
              variant="cyan"
              bottom={{ base: "12%", lg: "72%" }}
              right={{ base: "-50%", lg: "-10%" }}
              boxSize={{ base: "50%", sm: "60%", lg: "80%" }}
              opacity={{ base: 0.7, sm: 0.8, lg: 1 }}
            />
            <RadialGradient
              variant="mint"
              bottom={{ base: "4%", md: "2%", lg: "2%" }}
              right={{ base: "-50%", lg: "-70%" }}
              w={{ base: "90%", lg: "120%" }}
              h={{ base: "50%", md: "60%", lg: "200%" }}
              opacity={{ base: 0.4, sm: 0.8, xl: 1 }}
            />
          </>
        }
      />

      <StackedLogos title="" customerRows={customerRows} my={{ base: 6, md: 16, lg: 20 }} />

      <Section center my={0} mb="huge">
        <SectionHeading
          eyebrow="Warehouse-native audience building"
          title="Build and use audiences, all on your own"
          maxW="3xl"
          mx="auto"
        />

        <GradientCard
          variant="green"
          mt={{ base: 8, md: 12, lg: 20 }}
          pt={{ base: 0, sm: 6, md: 12, lg: "60px" }}
          px={{ base: 0, sm: 6, md: 12, lg: "60px" }}
          overflow="hidden"
          width="100%"
        >
          <AspectRatio ratio={16 / 9} bg="white" borderTopRadius="lg" overflow="hidden" w="100%" maxW="4xl" mx="auto">
            <AnimationVideo
              mp4="https://cdn.sanity.io/files/pwmfmi47/production/fcffe6d5459959f713e1374524a7c7908c5a53f0.mp4"
              poster="https://cdn.sanity.io/images/pwmfmi47/production/ffa862184a5abe638c3e5c82d74ead49963e6ae6-1348x792.webp"
              border="none"
              borderRadius="none"
            />
          </AspectRatio>
        </GradientCard>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={5} mt={{ base: 7, lg: 10 }}>
          <ImageFeatureCard
            title={`Personalize email \nand website experiences`}
            description="Deliver consistent, hyper-personalized experiences to audiences across channels. "
            image={feature1Image}
          />

          <ImageFeatureCard
            title={`Target \nadvertisements`}
            description="Target your best prospects or suppress less valuable audiences on advertising platforms like Meta, Google, TikTok, Snapchat, and more."
            image={feature2Image}
          />

          <ImageFeatureCard
            title={`Drive \nexperimentation`}
            description="Set up, execute, and measure A/B tests and hold-out groups on your own, but with the accuracy of an analytics team."
            image={feature3Image}
          />
        </SimpleGrid>
      </Section>

      <Section bg="forest">
        <CenteredQuote
          theme="dark"
          quoteText="This might be one of the greatest inventions for technical marketers since the advent of legacy CDPs back in 2015."
          author={{
            name: "Austin Hay",
            job: "Head of Marketing Technology",
          }}
          company={{
            name: "Ramp",
            logo: "https://cdn.sanity.io/images/pwmfmi47/production/acaf90882a1ded8a3796cc3972219355a42d009c-270x72.svg",
          }}
          cta={{ href: "/customers/ramp", text: "Read customer story" }}
        />
      </Section>

      <Section my={0} mt="huge">
        <SectionHeading
          eyebrow="Customer Studio features"
          title="Create audiences without limitations"
          description="Leverage every data point you have by activating data directly from your cloud data warehouse. Don’t limit yourself to the rigid structures of traditional Customer Data Platforms."
        />

        <Flex direction="column" gap={{ base: 20, lg: 24, xl: 28 }} mt={{ base: 20, lg: 24, xl: 28 }} w="100%">
          <ProductPair
            align="right"
            eyebrow="Activation"
            title={"Sync your audiences to any \nCRM, email, or ad platform"}
            description={`Sync updated audiences and customer profiles to ${integrationsCount}+ tools. Personalize and target your marketing across all of your channels with all of your data.`}
            actionUrl="/integrations"
            actionText="See Hightouch integrations"
            mobileImage={activateMobile}
            animationElement={<ActivationAnimation />}
            animationProps={{ minHeight: { base: "380px", lg: "400px" } }}
            gap={productPairGap}
          />

          <ProductPair
            id="journeys"
            theme="blue"
            align="left"
            eyebrow="Journeys"
            title={"Orchestrate complex \ncustomer journeys"}
            description="Easily prioritize campaigns across your audiences. Ensure that customers experience cohesive marketing and get the right message at the right time."
            mobileImage={journeysMobile}
            animationElement={<JourneysAnimation />}
            animationProps={{ minHeight: { base: "300px", md: "340px", lg: "400px" } }}
            gap={productPairGap}
          />

          <ProductPair
            align="right"
            eyebrow="Splits"
            title={"Run multivariate testing \nacross channels"}
            description="Build randomized audience splits directly within Hightouch to centrally manage your multivariate testing across channels. Never upload a testing CSV again."
            actionUrl="/blog/audience-splits"
            actionText="Learn about Splits"
            mobileImage={splitsMobile}
            animationElement={<SplitsAnimation />}
            animationProps={{ minHeight: { base: "320px", lg: "390px", xl: "470px" } }}
            gap={productPairGap}
          />

          <ProductPair
            theme="blue"
            align="left"
            eyebrow="Insights"
            title={"Build the right audience \nevery time"}
            description="Explore audience sizes and makeup as you build them. Visualize the distribution of user characteristics within audiences, or check audience overlaps to better understand who you are targeting."
            actionUrl="/blog/audience-breakdowns"
            actionText="Explore Audience Breakdowns"
            mobileImage={insightsMobile}
            animationElement={<InsightsAnimation />}
            animationProps={{ minHeight: { base: "380px", lg: "410px", xl: "470px" } }}
            gap={productPairGap}
          />

          <ProductPair
            id="traits"
            align="right"
            eyebrow="Traits"
            title={"Create new attributes for \npersonalization in seconds"}
            description="Easily define new calculated fields for personalization and segmentation like ‘Last Page Viewed’ or ‘Lifetime Value’ without code."
            actionUrl="/blog/introducing-hightouch-traits"
            actionText="Explore Custom Traits"
            mobileImage={traitsMobile}
            animationElement={<TraitsAnimation />}
            animationProps={{ minHeight: { base: "380px", lg: "410px", xl: "470px" } }}
            gap={productPairGap}
          />
        </Flex>
      </Section>

      <ComposableCdpDiagram
        variant="customer-studio"
        sectionTitle="Customer Studio is powered by your cloud data warehouse"
      />

      <DemoEmbedHalf />

      <SecurityFeatures />

      <Section>
        <SectionHeading title="See how leading teams activate their data" maxW="3xl" mx="auto" />

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={5} mt={{ base: 8, md: 12, lg: 20 }}>
          <QuoteCard
            hasBorder
            bg="the-zebra"
            logo="https://cdn.sanity.io/images/pwmfmi47/production/4af8106fe4444c6746d3862137b2d21bba6ae47b-545x168.png"
            quote="Hightouch is a great tool. My marketing team can now self-serve all our data syncs."
            author="Corey B"
            job="Marketing Ops"
            company="The Zebra"
            href="/customers/the-zebra"
          />

          <QuoteCard
            hasBorder
            bg="gorgias"
            logo="https://cdn.sanity.io/images/pwmfmi47/production/e62fd72fdffaa05f40e8251f137d416fad604a64-302x72.svg"
            quote="Hightouch allows us to target the same people and push audiences to different channels so the same people see the same ads across the Internet."
            author="Max Sutra"
            job="Growth and Demand Gen Lead"
            company="Gorgias"
            href="/customers/gorgias"
          />

          <QuoteCard
            hasBorder
            bg="lucidchart"
            logo="https://cdn.sanity.io/images/pwmfmi47/production/a2cefd7b22e1d57caba02e8a951242756d4017b8-609x164.png"
            quote="With Hightouch, we can trust that the same data that lives in our internal reports is the same data that is getting passed to our ad platforms."
            author="Mallory Reese-Bagley"
            job="Senior Marketing Operations Manager"
            company="Lucid"
            href="/customers/lucidchart"
          />
        </SimpleGrid>
      </Section>

      <DestinationMarquee destinations={destinations} />
    </Page>
  );
};

export default CustomerStudio;

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [page, announcementBanner, destinations, customerRows] = await Promise.all([
    getPage("/platform/customer-studio"),
    getAnnouncementBanner(),
    getSimpleIntegrations("destination"),
    getStackedLogos(),
  ]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      announcementBanner,
      destinations,
      customerRows,
    },
    revalidate: 60,
  };
};
